import { AlertTriangleIcon } from '@repo/studio-design-system-ui-icons/alerts-feedback/alert-triangle'
import * as React from 'react'
import { tw } from 'utils/classnames'

type MessageWindowVariants = 'default' | 'error-pink' | 'violet' | 'error-red' | '3d'

const MessageWindowVariantContext = React.createContext<MessageWindowVariants | null>(null)

function MessageWindowRoot({
  variant = 'default',
  children,
  className,
  ...props
}: { variant?: MessageWindowVariants } & React.ComponentProps<'div'>) {
  return (
    <MessageWindowVariantContext.Provider value={variant}>
      <div
        className={tw(
          'rounded-2 typography-14 flex flex-col items-center gap-4 border border-dashed p-4 text-center',
          variant === 'default' && 'border-gray-700 bg-gray-900 text-gray-100',
          variant === 'error-pink' && 'border-pink-500 bg-pink-500/15 text-gray-50',
          variant === 'error-red' &&
            'border-theme_semantic_status_error_border-color-regular text-theme_semantic_status_foreground-color-error-strong',
          variant === 'violet' && 'border-violet-500 bg-violet-500/15 text-gray-50',
          variant === '3d' && 'relative border-gray-700 bg-gray-1000 text-gray-100 p-3 gap-2',
          className,
        )}
        {...props}
      >
        {variant === '3d' ? <MessageWindow3DHighlights /> : null}
        {children}
      </div>
    </MessageWindowVariantContext.Provider>
  )
}

function MessageWindowIcon() {
  const variant = React.useContext(MessageWindowVariantContext)

  return (
    <div
      className={tw(
        'rounded-2 flex h-10 w-10 items-center justify-center',
        variant === 'default' && 'bg-violet-500',
        variant === 'error-pink' && 'bg-pink-500',
        variant === 'error-red' && 'bg-theme_semantic_status_error_background-color-regular',
        variant === 'violet' && 'bg-violet-500',
      )}
    >
      <AlertTriangleIcon className="text-service-white-white h-6 w-6" />
    </div>
  )
}

function MessageWindow3DHighlights() {
  const id = React.useId()

  return (
    <svg
      width="217"
      height="107"
      viewBox="0 0 217 107"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute h-full w-full top-0"
    >
      <g opacity="0.3" filter={`url(#filter0_f_${id})`}>
        <ellipse cx="50" cy="-34" rx="57.5" ry="57" fill="#8559F9" />
      </g>
      <g opacity="0.33" filter={`url(#filter1_f_${id})`}>
        <ellipse cx="175" cy="-34" rx="57.5" ry="57" fill="#FA5A84" />
      </g>
      <defs>
        <filter
          id={`filter0_f_${id}`}
          x="-62"
          y="-175"
          width="283"
          height="282"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="42" result={`effect1_foregroundBlur_${id}`} />
        </filter>
        <filter
          id={`filter1_f_${id}`}
          x="19"
          y="-175"
          width="283"
          height="282"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="42" result={`effect1_foregroundBlur_${id}`} />
        </filter>
      </defs>
    </svg>
  )
}

export const MessageWindow = Object.assign(MessageWindowRoot, {
  Icon: MessageWindowIcon,
})
